import { useEffect, useRef, useState } from "react"

import { useDebouncedCallback } from "../../../../hooks/use-debounced-callback.ts"
import { getLeadMatches } from "../../../Leads/api/services.ts"
import {
  type CustomerMatchInput,
  MATCH_DEBOUNCE_MS
} from "./use-customer-matches.ts"
import type { LeadMatch } from "../../../Leads/api/services.types.ts"
import {
  MIN_MATCHABLE_PHONE_DIGITS,
  normalizeEmailForMatch,
  phoneDigits
} from "./customer-match.ts"

// The floor every other search box in the product uses. It is the request
// count that costs here, not the payload: one of the statements behind this
// route runs on a connection that bypasses the pooler.
const MIN_MATCHABLE_NAME_LENGTH = 3

/**
 * Existing leads for the person being entered. Warn-only: a failed lookup
 * shows nothing rather than blocking, because a candidate list is advice and
 * its absence must never stop a lead being created.
 */
function useLeadMatches(typed: CustomerMatchInput) {
  const [matches, setMatches] = useState<LeadMatch[]>([])
  // Rows are acted on by index into this list, so a response landing out of
  // order would repoint the row the user is about to click.
  const latest = useRef(0)

  const lookup = useDebouncedCallback((input: CustomerMatchInput) => {
    void (async () => {
      const first = (input.first_name ?? "").trim()
      const last = (input.last_name ?? "").trim()
      const email = (input.email ?? "").trim()
      const phone = (input.phone ?? "").trim()

      const wantsName =
        first.length >= MIN_MATCHABLE_NAME_LENGTH ||
        last.length >= MIN_MATCHABLE_NAME_LENGTH
      const wantsEmail = normalizeEmailForMatch(email).includes("@")
      const wantsPhone = phoneDigits(phone).length >= MIN_MATCHABLE_PHONE_DIGITS

      const seq = ++latest.current

      if (!wantsName && !wantsEmail && !wantsPhone) {
        setMatches([])
        return
      }

      try {
        const response = await getLeadMatches({
          first_name: wantsName ? first || undefined : undefined,
          last_name: wantsName ? last || undefined : undefined,
          email: wantsEmail ? email : undefined,
          phone: wantsPhone ? phone : undefined
        })

        if (seq !== latest.current) return
        setMatches(response.ok ? (response.data ?? []) : [])
      } catch {
        if (seq !== latest.current) return
        setMatches([])
      }
    })()
  }, MATCH_DEBOUNCE_MS)

  const { first_name, last_name, email, phone } = typed

  useEffect(() => {
    lookup({ first_name, last_name, email, phone })
  }, [first_name, last_name, email, phone, lookup])

  return { matches }
}

export { useLeadMatches }
